import "./styles.css";
import type {
  BootstrapResponse,
  ClinicalReminderView,
  EncounterOrderView,
  EncounterOrdersResponse,
  EncounterRecordResponse,
  PatientTimelineResponse,
  PatientAssistantQueryResponse,
  PreviewVoiceChartingResponse,
  RegisterPatientRequest,
  WaitingPatient,
} from "../shared/contracts";
import type {
  ClinicalDocumentContent,
  ClinicalEventType,
  ClinicalTextColor,
  ClinicalTextSize,
} from "../shared/clinical";
import type { OrderLaterality, OrderModality } from "../shared/orders";
import { normalizeOrderSearch } from "../shared/orders";
import type { ClinicalFactSuggestion } from "../shared/voice-charting";
import { syntheticShoulderDialogue } from "../shared/voice-charting";
import type { PatientFlowStage } from "../shared/workflow";
import type { PatientClinicalAuditResponse } from "../shared/patient-audit";
import {
  emptyPatientFlowCounts,
  patientFlowStageLabels,
  patientFlowStages,
} from "../shared/workflow";
import { clinicalReminderTriggerLabels } from "../shared/reminders";
import {
  acknowledgeReminder,
  ApiError,
  completeEncounter,
  confirmOrder,
  createOrderDraft,
  loadBootstrap,
  loadEncounterOrders,
  loadEncounterRecord,
  loadPatientTimeline,
  loadPatientAudit,
  loadPatientReminders,
  previewVoiceCharting,
  queryPatientAssistant,
  registerPatient,
  saveClinicalNote,
  startEncounter,
  runAiCheck,
  updatePatientFlowStage,
} from "./api";
import {
  applyAutomaticKeywordEmphasis,
  applyInlineFormat,
  insertPlainTextAtSelection,
  renderClinicalContent,
  serializeClinicalEditor,
  type ClinicalInlineFormat,
} from "./clinical-editor";
import { previewFixture } from "./demo-fixture";
import {
  appendClinicalFactsToStructuredDraft,
  createStructuredClinicalDraft,
} from "./clinical-note-template";
import {
  playReminderTone,
  speakKorean,
  stopSpeaking,
} from "./audio-output";
import {
  loadWorkspacePreferences,
  preferencesForPreset,
  saveWorkspacePreferences,
  type OrderCatalogSort,
  type WorkspacePreset,
} from "./workspace-preferences";

function requireAppRoot(): HTMLDivElement {
  const element = document.querySelector<HTMLDivElement>("#app");
  if (!element) throw new Error("App root not found");
  return element;
}

const root = requireAppRoot();

let data: BootstrapResponse | null = null;
let selectedEncounterId: string | null = null;
let busy = false;
let apiConnected = true;
let notice: { kind: "ok" | "error" | "info"; text: string } | null = null;

let currentEncounterRecord: EncounterRecordResponse | null = null;
let clinicalLoading = false;
let clinicalError: string | null = null;
let clinicalRequestSequence = 0;

let patientAudit: PatientClinicalAuditResponse | null = null;
let patientAuditLoading = false;
let patientAuditError: string | null = null;
let patientAuditRequestSequence = 0;

let draftEncounterId: string | null = null;
let draftTitle = "";
let draftContent: ClinicalDocumentContent | null = null;
let draftVersion = 0;
let draftDirty = false;
let draftSavedAt: string | null = null;
let noteSaving = false;
let editorHint = "문장을 선택한 뒤 서식을 적용하십시오.";

let timelineOverview: PatientTimelineResponse | null = null;
let timelineResults: PatientTimelineResponse | null = null;
let timelineLoading = false;
let timelineError: string | null = null;
let timelineOpen = false;
let timelineSearchText = "";
let timelineType: ClinicalEventType | null = null;
let timelineRequestSequence = 0;
let timelineSearchTimer: number | null = null;

let encounterOrders: EncounterOrdersResponse | null = null;
let ordersLoading = false;
let ordersError: string | null = null;
let orderSearchText = "";
let orderLaterality: OrderLaterality = "right";
let orderModality: OrderModality = "xray";
let orderBodyRegion = "all";
let orderBusyId: string | null = null;
let voiceDraftOrderId: string | null = null;

let voiceResults: PreviewVoiceChartingResponse[] = [];
let voiceBusy = false;
let voiceError: string | null = null;
let contextTab: "clinical" | "assistant" | "reminders" = "clinical";

let queueOpen = false;
let queueFilter: PatientFlowStage | null = null;
let layoutOpen = false;
let mobileTab: "chart" | "orders" | "history" | "assistant" | "reminders" =
  "chart";
let workspacePreferences = loadWorkspacePreferences();

let assistantConversation: PatientAssistantQueryResponse[] = [];
let assistantQueryText = "";
let assistantBusy = false;
let assistantError: string | null = null;
let privateAudioConfirmed = false;

let reminders: ClinicalReminderView[] = [];
let remindersBusy = false;
let remindersError: string | null = null;
let reminderAlertOpen = false;

let recordOpen = false;
let recordLoading = false;
let recordError: string | null = null;
let recordDetail: EncounterRecordResponse | null = null;
let recordEncounterId: string | null = null;
let returnToTimeline = false;
const recordCache = new Map<string, EncounterRecordResponse>();

const escapeHtml = (value: unknown): string =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[character] ?? character,
  );

const statusLabel: Record<string, string> = {
  registered: "접수",
  waiting: "대기",
  called: "호출",
  planned: "진료 전",
  in_progress: "진료 중",
  completed: "완료",
  cancelled: "취소",
  draft: "작성 중",
  final: "서명 완료",
  amended: "수정 기록",
  entered_in_error: "오류 기록",
};

const roleLabel: Record<string, string> = {
  hospital_owner: "병원 소유자",
  admin: "관리자",
  doctor: "의사",
  nurse: "간호",
  reception: "접수",
  billing: "원무",
  radiology: "방사선",
  therapist: "물리치료",
  read_only: "조회 전용",
};

const actionLabel: Record<string, string> = {
  "patient.created": "환자 Master 생성",
  "reception.created": "접수·대기 등록",
  "encounter.started": "진료 시작",
  "encounter.completed": "진료 완료",
  "clinical_document.revised": "임상 기록 버전 저장",
  "order.drafted": "오더 초안 생성",
  "order.confirmed_and_queued": "오더 확정·검사실 접수",
  "voice.transcript_structured": "음성 문답 구조화",
  "reception.flow_stage_changed": "환자 흐름 변경",
  "clinical_assistant.answered": "존 임상 질의 응답",
  "clinical_ai_check.completed": "진료 종료 전 AI 체크",
  "clinical_reminder.acknowledged": "AI 체크 알림 확인",
};

const eventLabel: Record<ClinicalEventType, string> = {
  diagnosis: "병명",
  injection: "주사",
  procedure: "시술",
  test: "검사",
  treatment: "치료",
  course: "중요 경과",
  symptom: "특이증상",
  medical_history: "과거력",
};

const orderCategoryLabel: Record<string, string> = {
  imaging: "영상검사",
  laboratory: "피검사",
  procedure: "처치",
  medication: "약물",
  therapy: "치료",
  other: "기타",
};

const orderModalityLabel: Record<OrderModality, string> = {
  xray: "방사선",
  ct: "CT",
  mri: "MRI",
  ultrasound: "초음파",
  laboratory: "혈액검사",
  procedure: "시술",
  medication: "약물",
  therapy: "치료",
  other: "기타",
};

const orderStatusLabel: Record<string, string> = {
  draft: "초안",
  queued: "검사실 접수",
  in_progress: "진행 중",
  completed: "완료",
  cancelled: "취소",
};

const lateralityLabel: Record<OrderLaterality, string> = {
  right: "우측",
  left: "좌측",
  bilateral: "양측",
  not_applicable: "해당 없음",
};

const orderSortLabel: Record<OrderCatalogSort, string> = {
  frequency: "다빈도순",
  body_region: "인체 부위순",
  alphabetical: "가나다·알파벳순",
  shorthand: "약속기호순",
};

function formatKst(value: string, includeYear = false): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    ...(includeYear ? { year: "numeric" } : {}),
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function calculateAge(dateOfBirth: string): number | null {
  const birth = new Date(`${dateOfBirth}T00:00:00Z`);
  if (Number.isNaN(birth.valueOf())) return null;
  const todayParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date())
    .split("-")
    .map(Number);
  const [year, month, day] = todayParts;
  if (year === undefined || month === undefined || day === undefined) return null;
  let age = year - birth.getUTCFullYear();
  if (
    month < birth.getUTCMonth() + 1 ||
    (month === birth.getUTCMonth() + 1 && day < birth.getUTCDate())
  ) {
    age -= 1;
  }
  return age;
}

function currentPatient(): WaitingPatient | null {
  if (!data?.waiting.length) return null;
  return (
    data.waiting.find((patient) => patient.encounterId === selectedEncounterId) ??
    data.waiting[0] ??
    null
  );
}

function createDefaultDraft(_patient: WaitingPatient): ClinicalDocumentContent {
  return createStructuredClinicalDraft();
}

function patientCard(patient: WaitingPatient): string {
  const selected = patient.encounterId === currentPatient()?.encounterId;
  const age = calculateAge(patient.dateOfBirth);
  return `
    <button class="queue-card flow-${escapeHtml(patient.flowStage)} ${selected ? "selected" : ""}"
      data-action="select" data-encounter-id="${escapeHtml(patient.encounterId)}"
      aria-pressed="${selected}">
      <span class="queue-time">${escapeHtml(formatKst(patient.arrivedAt))}</span>
      <strong>${escapeHtml(patient.patientName)}</strong>
      <span>${escapeHtml(patient.chartNumber)} · ${age === null ? "연령 미상" : `${age}세`}</span>
      <span class="reason">${escapeHtml(patient.reason)}</span>
      <span class="status flow-status flow-${escapeHtml(patient.flowStage)}">
        ${escapeHtml(patientFlowStageLabels[patient.flowStage])}
      </span>
    </button>`;
}

function flowCounts(): Record<PatientFlowStage, number> {
  const counts = emptyPatientFlowCounts();
  for (const patient of data?.waiting ?? []) counts[patient.flowStage] += 1;
  return counts;
}

function renderFlowStrip(): string {
  const counts = flowCounts();
  return `<nav class="flow-strip" aria-label="오늘 환자 흐름 요약">
    <button type="button" class="flow-strip-title" data-action="open-queue" data-flow-stage="all">
      <span>대기환자 명단</span><b>${data?.waiting.length ?? 0}</b>
    </button>
    ${patientFlowStages
      .map(
        (stage) => `<button type="button" class="flow-stat flow-${stage}"
          data-action="open-queue" data-flow-stage="${stage}">
          <span>${patientFlowStageLabels[stage]}</span><b>${counts[stage]}</b>
        </button>`,
      )
      .join("")}
    <button type="button" class="layout-button" data-action="open-layout">화면 구성</button>
  </nav>`;
}

function auditRow(event: PatientClinicalAuditResponse["events"][number]): string {
  return `
    <li>
      <span class="audit-dot" aria-hidden="true"></span>
      <div>
        <strong>${escapeHtml(actionLabel[event.action] ?? event.action)}</strong>
        <span>${escapeHtml(event.actorDisplayName)} · ${escapeHtml(formatKst(event.occurredAt))}</span>
      </div>
    </li>`;
}

function eventLink(
  event: PatientTimelineResponse["events"][number],
  compact = false,
): string {
  return `
    <a class="history-event ${compact ? "compact" : ""} type-${event.type}"
      href="${escapeHtml(event.recordHref)}" data-action="open-record"
      data-encounter-id="${escapeHtml(event.sourceEncounterId)}">
      <span class="event-when">
        <time>${escapeHtml(formatKst(event.occurredAt, true))}</time>
        <b class="event-type">${escapeHtml(eventLabel[event.type])}</b>
      </span>
      <span class="event-copy">
        <strong>${escapeHtml(event.title)}</strong>
        <span>${escapeHtml(event.summary)}</span>
      </span>
      <span class="record-link">원기록 열기</span>
    </a>`;
}

function renderHistorySummary(): string {
  if (clinicalLoading && !timelineOverview) {
    return `<section class="history-card clinical-card loading-card">
      <div class="skeleton wide"></div><div class="skeleton"></div><div class="skeleton"></div>
    </section>`;
  }
  if (timelineError && !timelineOverview) {
    return `<section class="history-card clinical-card error-card">
      <div><span class="eyebrow">PREVIOUS COURSE</span><h3>이전 중요 경과</h3></div>
      <p>${escapeHtml(timelineError)}</p>
    </section>`;
  }
  const timeline = timelineOverview;
  if (!timeline || timeline.summary.total === 0) {
    return `<section class="history-card clinical-card">
      <div class="section-heading"><div><span class="eyebrow">PREVIOUS COURSE</span><h3>이전 중요 경과</h3></div></div>
      <p class="empty-copy">색인된 과거 병명·주사·검사·특이증상·과거력이 없습니다.</p>
    </section>`;
  }

  const countChips = (Object.entries(timeline.summary.byType) as [
    ClinicalEventType,
    number,
  ][])
    .filter(([, count]) => count > 0)
    .map(
      ([type, count]) =>
        `<button type="button" class="count-chip type-${type}"
          data-action="open-timeline-filter" data-event-type="${type}">
          ${eventLabel[type]} <b>${count}</b>
        </button>`,
    )
    .join("");

  return `<section class="history-card clinical-card">
    <div class="section-heading history-heading">
      <div><span class="eyebrow">RETURN VISIT SUMMARY</span><h3>이전 중요 경과</h3></div>
      <button class="secondary" data-action="open-timeline">
        검색·전체보기 <b>${timeline.summary.total}</b>
      </button>
    </div>
    <div class="count-chips">${countChips}</div>
    <div class="history-preview">${timeline.events.slice(0, 3).map((event) => eventLink(event, true)).join("")}</div>
  </section>`;
}

function renderTestResults(): string {
  const tests = timelineOverview?.events.filter((event) => event.type === "test") ?? [];
  return `<section class="results-card clinical-card context-section">
    <div class="section-heading">
      <div><span class="eyebrow">RESULTS</span><h3>검사 결과</h3></div>
      <button type="button" class="text-button" data-action="open-timeline-filter" data-event-type="test">
        검사 전체 ${tests.length}
      </button>
    </div>
    <div class="result-list">
      ${
        tests.length
          ? tests
              .slice(0, 3)
              .map(
                (event) => `<button type="button" class="result-row" data-action="open-record"
                  data-encounter-id="${escapeHtml(event.sourceEncounterId)}">
                  <time>${escapeHtml(formatKst(event.occurredAt, true))}</time>
                  <strong>${escapeHtml(event.title)}</strong>
                  <span>${escapeHtml(event.summary)}</span>
                </button>`,
              )
              .join("")
          : '<p class="empty-copy">표시할 과거 검사 결과가 없습니다.</p>'
      }
    </div>
  </section>`;
}

function renderOrderRow(order: EncounterOrderView): string {
  const laterality = order.laterality ? lateralityLabel[order.laterality] : "좌우 미확인";
  const canConfirm =
    order.status === "draft" &&
    (!order.requiresLaterality || (order.laterality !== null && order.laterality !== "not_applicable"));
  return `<li class="order-row status-${order.status}">
    <div class="order-row-main">
      <span class="order-category">${escapeHtml(orderCategoryLabel[order.category] ?? order.category)}</span>
      <strong>${escapeHtml(order.displayName)}</strong>
      <span>${escapeHtml(laterality)} · ${escapeHtml(orderStatusLabel[order.status] ?? order.status)}</span>
    </div>
    ${
      order.status === "draft"
        ? `<button type="button" class="confirm-order" data-action="confirm-order"
            data-order-id="${escapeHtml(order.id)}" ${!canConfirm || orderBusyId === order.id ? "disabled" : ""}>
            ${orderBusyId === order.id ? "확정 중…" : "의사 확인·검사실 접수"}
          </button>`
        : `<span class="worklist-badge">${order.destination === "radiology" ? "방사선실" : order.destination === "laboratory" ? "검사실" : "작업목록"} 대기</span>`
    }
  </li>`;
}

function renderOrderCenter(patient: WaitingPatient): string {
  const editable = apiConnected && patient.encounterStatus === "in_progress";
  const normalizedQuery = normalizeOrderSearch(orderSearchText);
  const allCatalog = encounterOrders?.catalog ?? [];
  const modalityCatalog = normalizedQuery
    ? allCatalog
    : allCatalog.filter((item) => item.modality === orderModality);
  const bodyRegions = Array.from(
    new Set(
      allCatalog
        .filter((item) => item.modality === orderModality)
        .map((item) => item.bodyRegion)
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right, "ko-KR"));
  const catalog = modalityCatalog
    .filter((item) => {
      if (
        !normalizedQuery &&
        orderBodyRegion !== "all" &&
        item.bodyRegion !== orderBodyRegion
      ) {
        return false;
      }
      if (!normalizedQuery) return true;
      return normalizeOrderSearch(
        `${item.code} ${item.displayName} ${item.searchText} ${item.shorthand} ${item.bodyRegion}`,
      ).includes(normalizedQuery);
    })
    .sort((left, right) => {
      if (workspacePreferences.orderSort === "frequency") {
        return left.frequencyRank - right.frequencyRank ||
          left.displayName.localeCompare(right.displayName, "ko-KR");
      }
      if (workspacePreferences.orderSort === "body_region") {
        return left.bodyRegion.localeCompare(right.bodyRegion, "ko-KR") ||
          left.frequencyRank - right.frequencyRank;
      }
      if (workspacePreferences.orderSort === "shorthand") {
        return left.shorthand.localeCompare(right.shorthand, "en") ||
          left.displayName.localeCompare(right.displayName, "ko-KR");
      }
      return left.displayName.localeCompare(right.displayName, "ko-KR");
    });
  const orders = encounterOrders?.orders ?? [];
  const voiceDraft = orders.find(
    (order) => order.id === voiceDraftOrderId && order.status === "draft",
  );

  return `<section class="orders-card clinical-card context-section" data-mobile-section="orders">
    <div class="section-heading">
      <div><span class="eyebrow">SAFE ORDER ENTRY</span><h3>검사·치료 오더</h3></div>
      <span class="order-count">현재 ${orders.length}</span>
    </div>
    <nav class="order-modality-tabs" aria-label="검사 종류">
      ${(Object.keys(orderModalityLabel) as OrderModality[])
        .map(
          (modality) => `<button type="button" class="${orderModality === modality && !normalizedQuery ? "active" : ""}"
            data-action="order-modality" data-modality="${modality}">
            ${orderModalityLabel[modality]} <b>${allCatalog.filter((item) => item.modality === modality).length}</b>
          </button>`,
        )
        .join("")}
    </nav>
    <div class="order-search-row">
      <input id="order-search" type="search" value="${escapeHtml(orderSearchText)}"
        placeholder="검사명·부위·SAPL·CBC" autocomplete="off"
        ${editable ? "" : "disabled"} />
      <select id="order-laterality" aria-label="오더 좌우" ${editable ? "" : "disabled"}>
        ${(["right", "left", "bilateral", "not_applicable"] as OrderLaterality[])
          .map(
            (value) =>
              `<option value="${value}" ${orderLaterality === value ? "selected" : ""}>${lateralityLabel[value]}</option>`,
          )
          .join("")}
      </select>
      <select id="order-sort" aria-label="오더 정렬">
        ${(Object.keys(orderSortLabel) as OrderCatalogSort[])
          .map(
            (sort) => `<option value="${sort}" ${workspacePreferences.orderSort === sort ? "selected" : ""}>${orderSortLabel[sort]}</option>`,
          )
          .join("")}
      </select>
    </div>
    ${
      normalizedQuery || bodyRegions.length === 0
        ? ""
        : `<div class="body-region-chips">
            <button type="button" class="${orderBodyRegion === "all" ? "active" : ""}"
              data-action="order-body-region" data-body-region="all">전체 부위</button>
            ${bodyRegions
              .map(
                (region) => `<button type="button" class="${orderBodyRegion === region ? "active" : ""}"
                  data-action="order-body-region" data-body-region="${escapeHtml(region)}">${escapeHtml(region)}</button>`,
              )
              .join("")}
          </div>`
    }
    ${ordersError ? `<p class="inline-error">${escapeHtml(ordersError)}</p>` : ""}
    ${
      ordersLoading
        ? '<div class="mini-loading"><div class="spinner"></div><span>오더 원장을 불러오는 중</span></div>'
        : `<div class="order-catalog" aria-label="오더 검색 결과">
            ${
              catalog.length
                ? catalog
                    .slice(0, 12)
                    .map(
                      (item) => `<button type="button" class="catalog-item"
                        data-action="add-order" data-catalog-id="${escapeHtml(item.id)}" ${editable ? "" : "disabled"}>
                        <span>${escapeHtml(item.bodyRegion || orderModalityLabel[item.modality])}</span>
                        <strong>${escapeHtml(item.displayName)}</strong>
                        <small><b>${escapeHtml(item.shorthand || item.code)}</b> · ${escapeHtml(item.code)}${item.requiresLaterality ? " · 좌우 필수" : ""}</small>
                      </button>`,
                    )
                    .join("")
                : '<p class="empty-copy">검색 조건에 맞는 오더가 없습니다.</p>'
            }
          </div>`
    }
    <div class="voice-order-demo">
      <span><b>가상 음성:</b> “엑스레이, 우측 Shoulder AP Lateral” → 초안 생성</span>
      <div>
        <button type="button" data-action="run-voice-order-draft" ${editable && !orderBusyId ? "" : "disabled"}>
          말해서 검사 생성
        </button>
        <button type="button" class="voice-order-confirm" data-action="confirm-voice-order"
          ${editable && voiceDraft && !orderBusyId ? "" : "disabled"}>
          “오더 완료” · 의사 확정
        </button>
      </div>
    </div>
    <ol class="current-orders">
      ${orders.length ? orders.map(renderOrderRow).join("") : '<li class="empty-copy">현재 진료의 오더가 없습니다.</li>'}
    </ol>
    <p class="order-policy">말하면 검사 항목만 초안으로 생성됩니다. 의사가 “오더 완료” 또는 확정 버튼을 눌러야 방사선실·검사실로 전송됩니다.</p>
  </section>`;
}

function renderVoiceCharting(patient: WaitingPatient): string {
  const editable = apiConnected && patient.encounterStatus === "in_progress";
  const latestResult = voiceResults.at(-1) ?? null;
  const allFacts = voiceResults.flatMap((result) => result.facts);
  const summaryText =
    allFacts.length > 0
      ? `누적 ${allFacts.length}개 · ${voiceResults.length}회`
      : "결과·테스트 접힘";

  return `<details class="voice-card clinical-card voice-inline-details">
    <summary>
      <span class="voice-inline-title">
        <span class="eyebrow">VOICE SUPPORT</span>
        <strong>음성진료 구조화</strong>
        <small>필요할 때만 결과·테스트 펼치기</small>
      </span>
      <span class="voice-status ${allFacts.length > 0 ? "ready" : "pending"}">${summaryText}</span>
    </summary>
    <div class="voice-inline-body">
      <div class="voice-actions">
        <button type="button" class="microphone-button" data-action="run-voice-preview"
          ${editable && !voiceBusy ? "" : "disabled"}>
          <span aria-hidden="true">●</span>${voiceBusy ? "문답 구조화 중…" : "가상 문답 추가 반영"}
        </button>
        <button type="button" class="live-microphone" data-action="open-clinical-microphone"
          ${editable ? "" : "disabled"}>
          실제 마이크 열기
        </button>
      </div>
      <p class="voice-policy">고정된 6개 항목 제한 없이 구조화된 음성 결과를 기존 차트 구획에 계속 누적합니다. 실제 녹음은 다음 보안 전사 단계에서 구조화되며, 저장 전 의료진 확인이 필요합니다.</p>
      ${voiceError ? `<p class="inline-error">${escapeHtml(voiceError)}</p>` : ""}
      ${
        latestResult
          ? `<details class="transcript-panel">
              <summary>최근 전사 원문 보기 · 전체 ${voiceResults.length}회</summary>
              <p>${escapeHtml(latestResult.segment.rawText)}</p>
            </details>
            <div class="fact-grid">
              ${allFacts
                .map(
                  (fact) => `<article class="fact-card">
                    <span>${escapeHtml(fact.label)}</span>
                    <strong>${escapeHtml(fact.value)}</strong>
                    <small>근거: ${escapeHtml(fact.evidence.quote)}</small>
                  </article>`,
                )
                .join("")}
            </div>`
          : `<div class="voice-example">
              <strong>차트 직접 반영 구획</strong>
              <span>Chief Complaint · Onset · Vector History · History · Physical Examination · Order · Procedure</span>
            </div>`
      }
    </div>
  </details>`;
}

function renderAssistant(): string {
  const quickQuestions = [
    "이 환자 3번째 히루안 주사 언제?",
    "이 환자 총 주사횟수는?",
    "이 환자 3년 전 내원은 무엇 때문이었지?",
    "히루안주의 용법 및 금기사항은?",
    "존, 네 생각은?",
  ];
  const latest = assistantConversation.at(-1);
  return `<section class="assistant-card clinical-card context-section">
    <div class="assistant-hero">
      <span class="assistant-avatar">J</span>
      <div><span class="eyebrow">AI ASSISTANT</span><h3>존</h3><p>현재 환자 기록을 근거로 답하는 의사 보조</p></div>
      <button type="button" class="assistant-audio" data-action="speak-assistant"
        ${latest && privateAudioConfirmed ? "" : "disabled"}>답변 듣기</button>
    </div>
    <div class="assistant-warning">존의 답은 의사 검토용입니다. 진단·처방·검사 오더를 자동 확정하지 않습니다.</div>
    <div class="assistant-quick-questions">
      ${quickQuestions
        .map(
          (question) => `<button type="button" data-action="assistant-quick" data-query="${escapeHtml(question)}">
            ${escapeHtml(question)}
          </button>`,
        )
        .join("")}
    </div>
    <div class="assistant-conversation" aria-live="polite">
      ${
        assistantConversation.length
          ? assistantConversation
              .slice(-4)
              .map(
                (response) => `<article class="assistant-exchange">
                  <p class="doctor-message"><b>의사</b>${escapeHtml(response.query)}</p>
                  <div class="john-message">
                    <b>존</b><p>${escapeHtml(response.answerText)}</p>
                    <div class="assistant-answer-cards">
                      ${response.cards
                        .map(
                          (card) => `<section class="answer-card tone-${card.tone}">
                            <strong>${escapeHtml(card.title)}</strong><p>${escapeHtml(card.body)}</p>
                          </section>`,
                        )
                        .join("")}
                    </div>
                    ${
                      response.sources.length
                        ? `<details class="assistant-sources"><summary>답변 근거 ${response.sources.length}개</summary>
                            ${response.sources
                              .map((source) =>
                                source.href?.startsWith("#record=")
                                  ? `<a href="${escapeHtml(source.href)}" data-action="open-record"
                                      data-encounter-id="${escapeHtml(decodeURIComponent(source.href.slice(8)))}">${escapeHtml(source.label)}</a>`
                                  : source.href
                                    ? `<a href="${escapeHtml(source.href)}" target="_blank" rel="noreferrer">${escapeHtml(source.label)}</a>`
                                    : `<span>${escapeHtml(source.label)}</span>`,
                              )
                              .join("")}
                          </details>`
                        : ""
                    }
                  </div>
                </article>`,
              )
              .join("")
          : '<div class="assistant-empty">위 질문을 누르거나 직접 입력하십시오. 환자 기록과 약품 참고자료는 답변에서 분리 표시됩니다.</div>'
      }
      ${assistantBusy ? '<div class="mini-loading"><div class="spinner"></div><span>존이 근거를 확인 중입니다.</span></div>' : ""}
    </div>
    ${assistantError ? `<p class="inline-error">${escapeHtml(assistantError)}</p>` : ""}
    <form id="assistant-form" class="assistant-composer">
      <input id="assistant-query" name="query" maxlength="500" value="${escapeHtml(assistantQueryText)}"
        placeholder="존에게 현재 환자에 대해 질문" autocomplete="off" />
      <button type="submit" ${assistantBusy ? "disabled" : ""}>문자로 묻기</button>
      <button type="button" data-action="assistant-voice-preview" ${assistantBusy ? "disabled" : ""}>가상 음성 질문</button>
      <button type="button" disabled title="병원 서버의 일회성 WebRTC 연결 자격 구성 후 활성화됩니다.">실시간 마이크 준비 중</button>
    </form>
    <label class="private-audio-check">
      <input id="private-audio-confirm" type="checkbox" ${privateAudioConfirmed ? "checked" : ""} />
      의사 전용 블루투스 이어폰이 OS 기본 출력으로 연결됐음을 확인
    </label>
    <p class="audio-policy">브라우저만으로 청취자를 식별할 수 없으므로 자동 재생하지 않습니다. 이어폰 연결을 의사가 확인한 세션에서만 ‘답변 듣기’가 활성화됩니다.</p>
  </section>`;
}

function renderAiCheck(patient: WaitingPatient): string {
  const active = reminders.filter((reminder) => reminder.status === "active");
  return `<section class="reminder-card clinical-card context-section">
    <div class="section-heading">
      <div><span class="eyebrow">AI SAFETY CHECK</span><h3>진료 종료 전 AI 체크</h3></div>
      <span class="reminder-count">활성 ${active.length}</span>
    </div>
    <p class="reminder-intro">마지막 환자를 마치거나 검사 오더 후 누르면 빠진 문답·결과 확인·추후 재평가 항목을 저장합니다. 다음 차트에서 알림음과 창으로 다시 표시됩니다.</p>
    <button type="button" class="run-ai-check" data-action="run-ai-check"
      ${patient.encounterStatus === "in_progress" && !remindersBusy ? "" : "disabled"}>
      ${remindersBusy ? "AI 체크 중…" : "AI 체크 실행·알림 저장"}
    </button>
    ${remindersError ? `<p class="inline-error">${escapeHtml(remindersError)}</p>` : ""}
    <div class="reminder-list">
      ${
        reminders.length
          ? reminders
              .map(
                (reminder) => `<article class="reminder-item severity-${reminder.severity} status-${reminder.status}">
                  <div><span>${escapeHtml(clinicalReminderTriggerLabels[reminder.trigger])}</span>
                    <strong>${escapeHtml(reminder.title)}</strong></div>
                  <p>${escapeHtml(reminder.detail)}</p>
                  <small>${escapeHtml(formatKst(reminder.createdAt, true))} · ${reminder.status === "active" ? "확인 필요" : "확인함"}</small>
                  ${
                    reminder.status === "active"
                      ? `<button type="button" data-action="ack-reminder" data-reminder-id="${escapeHtml(reminder.id)}">확인함</button>`
                      : ""
                  }
                </article>`,
              )
              .join("")
          : '<p class="empty-copy">저장된 AI 체크 알림이 없습니다.</p>'
      }
    </div>
  </section>`;
}

function renderContextPanel(patient: WaitingPatient): string {
  return `<aside class="clinical-context" data-mobile-section="${contextTab === "clinical" ? "history" : contextTab}">
    <nav class="context-tabs" aria-label="진료 문맥 탭">
      <button type="button" class="${contextTab === "clinical" ? "active" : ""}" data-action="context-tab" data-tab="clinical">
        과거기록·검사결과
      </button>
      <button type="button" class="${contextTab === "assistant" ? "active" : ""}" data-action="context-tab" data-tab="assistant">
        AI 어시스턴트 · 존
      </button>
      <button type="button" class="${contextTab === "reminders" ? "active" : ""}" data-action="context-tab" data-tab="reminders">
        AI 체크 ${reminders.filter((reminder) => reminder.status === "active").length || ""}
      </button>
    </nav>
    ${
      contextTab === "assistant"
        ? renderAssistant()
        : contextTab === "reminders"
          ? renderAiCheck(patient)
          : `${renderHistorySummary()}${renderTestResults()}`
    }
  </aside>`;
}

function renderWorkflow(patient: WaitingPatient): string {
  const canStart = apiConnected && patient.encounterStatus === "planned";
  const canComplete = apiConnected && patient.encounterStatus === "in_progress";
  return `<section class="session-card clinical-card">
    <div class="section-heading">
      <div><span class="eyebrow">CURRENT ENCOUNTER</span><h3>진료 Session</h3></div>
      <span class="version-chip">V0.5C</span>
    </div>
    <div class="steps" aria-label="진료 흐름">
      <div class="step done"><b>1</b><span>접수</span></div>
      <div class="step done"><b>2</b><span>대기</span></div>
      <div class="step ${canComplete ? "active" : patient.encounterStatus === "completed" ? "done" : ""}"><b>3</b><span>진료</span></div>
      <div class="step ${patient.encounterStatus === "completed" ? "done" : ""}"><b>4</b><span>완료</span></div>
    </div>
    <div class="action-row">
      <button class="primary" data-action="start" ${!canStart || busy ? "disabled" : ""}>
        ${busy && canStart ? "처리 중…" : "진료 시작"}
      </button>
      <button class="complete" data-action="complete" ${!canComplete || busy ? "disabled" : ""}>
        ${busy && canComplete ? "처리 중…" : "의료진 확인 후 진료 완료"}
      </button>
    </div>
    ${apiConnected ? "" : '<p class="connection-warning">Preview D1 연결 전이라 상태 변경 버튼이 잠겨 있습니다.</p>'}
  </section>`;
}

function renderClinicalEditor(patient: WaitingPatient): string {
  if (clinicalLoading && !draftContent) {
    return `<section class="note-card clinical-card loading-card">
      <div class="skeleton wide"></div><div class="skeleton tall"></div>
    </section>`;
  }
  const content = draftContent ?? createDefaultDraft(patient);
  const editable = apiConnected && patient.encounterStatus === "in_progress";
  const saveState = noteSaving
    ? "저장 중…"
    : draftDirty
      ? `미저장 변경 · 현재 v${draftVersion}`
      : draftSavedAt
        ? `${formatKst(draftSavedAt)} 저장 · v${draftVersion}`
        : draftVersion > 0
          ? `저장됨 · v${draftVersion}`
          : "아직 저장되지 않은 초안";

  return `<section class="note-card clinical-card">
    <div class="section-heading note-heading">
      <div><span class="eyebrow">STRUCTURED CLINICAL NOTE</span><h3>진료 기록</h3></div>
      <span id="note-save-state" class="save-state ${draftDirty ? "dirty" : ""}">${escapeHtml(saveState)}</span>
    </div>
    <label class="note-title-label">기록 제목
      <input id="note-title" value="${escapeHtml(draftTitle)}" maxlength="120" placeholder="기록 제목 입력" ${editable ? "" : "disabled"} />
    </label>
    <div class="format-toolbar" role="toolbar" aria-label="임상 기록 글자 서식">
      <div class="format-group">
        <button type="button" class="format-button bold-button" data-action="format-bold" title="선택 문장 굵게" ${editable ? "" : "disabled"}>B</button>
        <button type="button" class="format-button italic-button" data-action="format-italic" title="선택 문장 기울임" ${editable ? "" : "disabled"}>I</button>
        <button type="button" class="format-button keyword-button" data-action="format-keyword" ${editable ? "" : "disabled"}>핵심어</button>
      </div>
      <label>글자색
        <select id="format-color" ${editable ? "" : "disabled"}>
          <option value="">색 선택</option>
          <option value="blue">파랑</option><option value="red">빨강</option>
          <option value="green">초록</option><option value="amber">주황</option>
          <option value="violet">보라</option>
        </select>
      </label>
      <label>글자 크기
        <select id="format-size" ${editable ? "" : "disabled"}>
          <option value="">크기 선택</option>
          <option value="small">작게</option><option value="normal">보통</option>
          <option value="large">크게</option><option value="xlarge">매우 크게</option>
        </select>
      </label>
      <button type="button" class="auto-keyword" data-action="auto-keywords" ${editable ? "" : "disabled"}>핵심단어 자동 굵게</button>
      <button type="button" class="save-note" data-action="save-note" ${editable && draftDirty && !noteSaving ? "" : "disabled"}>기록 저장</button>
    </div>
    <p id="editor-hint" class="editor-hint">${escapeHtml(editorHint)}</p>
    <div id="clinical-editor" class="clinical-editor ${editable ? "editable" : "read-only"}"
      contenteditable="${editable ? "true" : "false"}" spellcheck="true"
      data-encounter-id="${escapeHtml(patient.encounterId)}"
      aria-label="진료 기록 편집 영역">${renderClinicalContent(content)}</div>
    <button type="button" class="voice-care-launch" data-action="open-clinical-microphone"
      ${editable ? "" : "disabled"}>
      <span aria-hidden="true">🎙</span><strong>음성진료</strong><small>마이크 열기 · 녹음 후 보안 전사 연결</small>
    </button>
    ${
      editable
        ? '<p class="editor-policy">허용된 구조화 서식만 저장되며 원본 HTML은 저장하지 않습니다.</p>'
        : `<p class="editor-lock">${patient.encounterStatus === "planned" ? "진료 시작 후 기록을 편집할 수 있습니다." : "완료된 진료는 이 화면에서 직접 변경하지 않습니다."}</p>`
    }
    ${clinicalError ? `<p class="inline-error">${escapeHtml(clinicalError)}</p>` : ""}
  </section>`;
}

function renderPatientWorkspace(patient: WaitingPatient | null): string {
  if (!patient) {
    return `<section class="empty-state">
      <div class="empty-icon">✓</div>
      <h2>대기 환자가 없습니다</h2>
      <p>새 환자를 접수하면 이 화면에서 진료 Session을 시작할 수 있습니다.</p>
    </section>`;
  }

  const age = calculateAge(patient.dateOfBirth);
  return `
    <section class="patient-header">
      <div>
        <span class="eyebrow">${escapeHtml(patient.chartNumber)}</span>
        <h2>${escapeHtml(patient.patientName)}</h2>
        <p>${escapeHtml(patient.dateOfBirth)} · ${age === null ? "연령 미상" : `${age}세`} ·
          ${patient.sexAtBirth === "male" ? "남" : patient.sexAtBirth === "female" ? "여" : "미상"}</p>
      </div>
      <span class="status large flow-${escapeHtml(patient.flowStage)}">
        ${escapeHtml(patientFlowStageLabels[patient.flowStage])} · ${escapeHtml(statusLabel[patient.encounterStatus] ?? patient.encounterStatus)}
      </span>
    </section>

    <section class="visit-summary">
      <div><span>내원 사유</span><strong>${escapeHtml(patient.reason)}</strong></div>
      <div><span>도착</span><strong>${escapeHtml(formatKst(patient.arrivedAt))}</strong></div>
      <div><span>접수 상태</span><strong>${escapeHtml(statusLabel[patient.receptionStatus] ?? patient.receptionStatus)}</strong></div>
    </section>

    <div class="clinical-cockpit" data-mobile-tab="${mobileTab}"
      style="--chart-fr:${(workspacePreferences.chartPercent / 44).toFixed(2)}fr">
      <div class="current-chart-column" data-mobile-section="chart">
        ${renderClinicalEditor(patient)}
        ${renderVoiceCharting(patient)}
        ${renderWorkflow(patient)}
      </div>
      <aside class="order-column" data-mobile-section="orders">${renderOrderCenter(patient)}</aside>
      ${renderContextPanel(patient)}
    </div>`;
}

function registerForm(): string {
  return `
    <details class="register-panel">
      <summary>+ 가상환자 접수</summary>
      <form id="register-form">
        <label>차트번호<input name="chartNumber" required maxlength="32" placeholder="DEMO-1002" /></label>
        <label>환자명<input name="patientName" required maxlength="100" placeholder="가상 홍길동" /></label>
        <div class="form-row">
          <label>생년월일<input name="dateOfBirth" type="date" required /></label>
          <label>성별<select name="sexAtBirth"><option value="unknown">미상</option><option value="male">남</option><option value="female">여</option></select></label>
        </div>
        <label>내원 사유<input name="reason" required maxlength="200" placeholder="좌측 무릎 통증" /></label>
        <button class="primary" type="submit" ${busy || !apiConnected ? "disabled" : ""}>접수·대기 등록</button>
        <p class="form-warning">가상정보만 입력하십시오. 실제 환자정보 입력 금지.</p>
      </form>
    </details>`;
}

function renderTimelineDialog(patient: WaitingPatient | null): string {
  if (!patient) return "";
  const result = timelineResults ?? timelineOverview;
  const filters: (ClinicalEventType | null)[] = [
    null,
    "diagnosis",
    "injection",
    "procedure",
    "test",
    "treatment",
    "course",
    "symptom",
    "medical_history",
  ];
  const filterButtons = filters
    .map((type) => {
      const count = type ? (timelineOverview?.summary.byType[type] ?? 0) : (timelineOverview?.summary.total ?? 0);
      return `<button type="button" class="timeline-filter ${timelineType === type ? "active" : ""}"
        data-action="timeline-filter" data-event-type="${type ?? "all"}">
        ${type ? eventLabel[type] : "전체"} <b>${count}</b>
      </button>`;
    })
    .join("");

  let body = "";
  if (timelineLoading) body = '<div class="dialog-loading"><div class="spinner"></div><p>중요 경과를 검색하고 있습니다.</p></div>';
  else if (timelineError) body = `<p class="dialog-error">${escapeHtml(timelineError)}</p>`;
  else if (!result?.events.length) body = '<p class="dialog-empty">조건에 맞는 병명·주사·검사·특이증상·과거력이 없습니다.</p>';
  else body = `<div class="timeline-results">${result.events.map((event) => eventLink(event)).join("")}</div>`;

  return `<dialog id="timeline-dialog" class="clinical-dialog" aria-labelledby="timeline-title">
    <div class="dialog-header">
      <div><span class="eyebrow">SEARCHABLE CLINICAL HISTORY</span><h2 id="timeline-title">${escapeHtml(patient.patientName)} 중요 경과 검색</h2></div>
      <button class="dialog-close" data-action="close-timeline" aria-label="닫기">×</button>
    </div>
    <div class="timeline-searchbar">
      <label><span>병명·주사·검사·특이증상·과거력·핵심어 검색</span>
        <input id="timeline-search" type="search" maxlength="100" value="${escapeHtml(timelineSearchText)}"
          placeholder="예: 초음파, 견봉하 주사, 70% 호전" autocomplete="off" />
      </label>
    </div>
    <div class="timeline-filters">${filterButtons}</div>
    <div class="dialog-body">${body}</div>
  </dialog>`;
}

function renderRecordDialog(): string {
  let body = "";
  if (recordLoading) {
    body = '<div class="dialog-loading"><div class="spinner"></div><p>당시 원기록을 불러오는 중입니다.</p></div>';
  } else if (recordError) {
    body = `<p class="dialog-error">${escapeHtml(recordError)}</p>`;
  } else if (recordDetail) {
    const encounter = recordDetail.encounter;
    const documents = recordDetail.documents.length
      ? recordDetail.documents
          .map(
            (document) => `<article class="record-document">
              <div class="record-document-header">
                <div><span class="eyebrow">ORIGINAL RECORD · REVISION ${document.version}</span><h3>${escapeHtml(document.title)}</h3></div>
                <span class="document-status ${document.status}">${escapeHtml(statusLabel[document.status] ?? document.status)}</span>
              </div>
              <p class="record-meta">${escapeHtml(document.authorDisplayName)} · ${escapeHtml(formatKst(document.updatedAt, true))}</p>
              <div class="record-content">${renderClinicalContent(document.content)}</div>
            </article>`,
          )
          .join("")
      : '<p class="dialog-empty">이 진료 건에는 저장된 구조화 임상 기록이 없습니다.</p>';
    body = `<section class="record-visit-summary">
      <div><span>환자</span><strong>${escapeHtml(encounter.patientName)} · ${escapeHtml(encounter.chartNumber)}</strong></div>
      <div><span>진료일</span><strong>${escapeHtml(formatKst(encounter.arrivedAt, true))}</strong></div>
      <div><span>내원 사유</span><strong>${escapeHtml(encounter.reason)}</strong></div>
      <div><span>담당</span><strong>${escapeHtml(encounter.clinicianDisplayName ?? "미지정")}</strong></div>
    </section>${documents}`;
  }

  return `<dialog id="record-dialog" class="clinical-dialog record-dialog" aria-labelledby="record-title">
    <div class="dialog-header">
      <div><span class="eyebrow">LINKED SOURCE RECORD</span><h2 id="record-title">당시 진료 원기록</h2></div>
      <button class="dialog-close" data-action="close-record" aria-label="닫기">×</button>
    </div>
    <div class="dialog-body">${body}</div>
  </dialog>`;
}

function renderQueueDialog(): string {
  const stages = queueFilter ? [queueFilter] : [...patientFlowStages];
  return `<dialog id="queue-dialog" class="clinical-dialog queue-dialog" aria-labelledby="queue-title">
    <div class="dialog-header">
      <div><span class="eyebrow">PATIENT FLOW</span><h2 id="queue-title">대기환자 명단</h2></div>
      <button class="dialog-close" data-action="close-queue" aria-label="닫기">×</button>
    </div>
    <nav class="queue-dialog-filters">
      <button type="button" class="${queueFilter === null ? "active" : ""}" data-action="queue-filter" data-flow-stage="all">전체</button>
      ${patientFlowStages
        .map(
          (stage) => `<button type="button" class="${queueFilter === stage ? "active" : ""}"
            data-action="queue-filter" data-flow-stage="${stage}">${patientFlowStageLabels[stage]} ${flowCounts()[stage]}</button>`,
        )
        .join("")}
    </nav>
    <div class="dialog-body queue-groups">
      ${stages
        .map((stage) => {
          const patients = (data?.waiting ?? []).filter(
            (patient) => patient.flowStage === stage,
          );
          return `<section class="queue-group flow-${stage}">
            <h3>${patientFlowStageLabels[stage]} <b>${patients.length}</b></h3>
            <div class="queue-group-list">
              ${
                patients.length
                  ? patients
                      .map(
                        (patient) => `<article class="queue-dialog-card">
                          ${patientCard(patient)}
                          <label>단계 변경
                            <select class="flow-stage-select" data-reception-id="${escapeHtml(patient.receptionId)}"
                              data-current-stage="${patient.flowStage}">
                              ${patientFlowStages
                                .map(
                                  (candidate) => `<option value="${candidate}" ${candidate === patient.flowStage ? "selected" : ""}>${patientFlowStageLabels[candidate]}</option>`,
                                )
                                .join("")}
                            </select>
                          </label>
                        </article>`,
                      )
                      .join("")
                  : '<p class="empty-copy">해당 단계의 환자가 없습니다.</p>'
              }
            </div>
          </section>`;
        })
        .join("")}
      ${registerForm()}
    </div>
  </dialog>`;
}

function renderLayoutDialog(): string {
  const presets: Array<{ value: WorkspacePreset; label: string }> = [
    { value: "chart_focus", label: "차트 중심" },
    { value: "balanced", label: "균형" },
    { value: "order_focus", label: "오더 중심" },
  ];
  return `<dialog id="layout-dialog" class="clinical-dialog layout-dialog" aria-labelledby="layout-title">
    <div class="dialog-header">
      <div><span class="eyebrow">PERSONAL WORKSPACE</span><h2 id="layout-title">즐겨쓰는 화면 구성</h2></div>
      <button class="dialog-close" data-action="close-layout" aria-label="닫기">×</button>
    </div>
    <div class="dialog-body layout-settings">
      <section><h3>빠른 구성</h3><div class="layout-presets">
        ${presets
          .map(
            (preset) => `<button type="button" class="${workspacePreferences.preset === preset.value ? "active" : ""}"
              data-action="layout-preset" data-preset="${preset.value}">${preset.label}</button>`,
          )
          .join("")}
      </div></section>
      <label class="layout-range">진료 차트 면적 <b>${workspacePreferences.chartPercent}%</b>
        <input id="chart-percent" type="range" min="48" max="72" step="1" value="${workspacePreferences.chartPercent}" />
      </label>
      <label class="layout-check"><input id="desktop-queue-toggle" type="checkbox" ${workspacePreferences.showDesktopQueue ? "checked" : ""} /> PC에서 대기환자 상시 표시</label>
      <p>PC 기본값은 대기환자·진료차트·방사선/오더를 상시 표시하고, 과거기록·존·AI 체크는 오른쪽 탭에서 전환합니다. 선택은 이 브라우저에 저장됩니다.</p>
    </div>
  </dialog>`;
}

function renderReminderAlertDialog(patient: WaitingPatient | null): string {
  const active = reminders.filter((reminder) => reminder.status === "active");
  if (!patient || active.length === 0) return "";
  return `<dialog id="reminder-alert-dialog" class="clinical-dialog reminder-alert-dialog" aria-labelledby="reminder-alert-title">
    <div class="dialog-header warning-header">
      <div><span class="eyebrow">AI CHECK REMINDER</span><h2 id="reminder-alert-title">${escapeHtml(patient.patientName)} 중요 확인사항</h2></div>
      <button class="dialog-close" data-action="close-reminder-alert" aria-label="닫기">×</button>
    </div>
    <div class="dialog-body reminder-alert-list">
      ${active
        .map(
          (reminder) => `<article class="reminder-item severity-${reminder.severity}">
            <span>${escapeHtml(clinicalReminderTriggerLabels[reminder.trigger])}</span>
            <strong>${escapeHtml(reminder.title)}</strong><p>${escapeHtml(reminder.detail)}</p>
            <button type="button" data-action="ack-reminder" data-reminder-id="${escapeHtml(reminder.id)}">확인함</button>
          </article>`,
        )
        .join("")}
      <button type="button" class="secondary" data-action="open-reminders-tab">AI 체크 탭에서 전체 보기</button>
    </div>
  </dialog>`;
}

function renderMobileNav(): string {
  const items: Array<{ tab: typeof mobileTab; label: string }> = [
    { tab: "chart", label: "진료차트" },
    { tab: "orders", label: "검사오더" },
    { tab: "history", label: "과거기록" },
    { tab: "assistant", label: "존" },
    { tab: "reminders", label: "AI 체크" },
  ];
  return `<nav class="mobile-clinical-nav" aria-label="모바일 진료 화면">
    ${items
      .map(
        (item) => `<button type="button" class="${mobileTab === item.tab ? "active" : ""}"
          data-action="mobile-tab" data-tab="${item.tab}">${item.label}</button>`,
      )
      .join("")}
  </nav>`;
}

function captureDraftFromDom(): void {
  const editor = document.querySelector<HTMLElement>("#clinical-editor");
  if (!editor || !draftEncounterId || editor.dataset.encounterId !== draftEncounterId) return;
  try {
    draftContent = serializeClinicalEditor(editor);
  } catch {
    // A transient browser editing node is normalized on the next explicit input/save.
  }
}

function syncDialogs(): void {
  const timelineDialog = document.querySelector<HTMLDialogElement>("#timeline-dialog");
  if (timelineDialog) {
    if (timelineOpen && !timelineDialog.open) timelineDialog.showModal();
    if (!timelineOpen && timelineDialog.open) timelineDialog.close();
  }
  const recordDialog = document.querySelector<HTMLDialogElement>("#record-dialog");
  if (recordOpen && !recordDialog?.open) recordDialog?.showModal();
  if (!recordOpen && recordDialog?.open) recordDialog.close();
  const queueDialog = document.querySelector<HTMLDialogElement>("#queue-dialog");
  if (queueOpen && !queueDialog?.open) queueDialog?.showModal();
  if (!queueOpen && queueDialog?.open) queueDialog.close();
  const layoutDialog = document.querySelector<HTMLDialogElement>("#layout-dialog");
  if (layoutOpen && !layoutDialog?.open) layoutDialog?.showModal();
  if (!layoutOpen && layoutDialog?.open) layoutDialog.close();
  const reminderDialog = document.querySelector<HTMLDialogElement>(
    "#reminder-alert-dialog",
  );
  if (reminderAlertOpen && !reminderDialog?.open) reminderDialog?.showModal();
  if (!reminderAlertOpen && reminderDialog?.open) reminderDialog.close();
  if (timelineOpen) {
    const input = document.querySelector<HTMLInputElement>("#timeline-search");
    if (input && document.activeElement === document.body) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }
}

function render(): void {
  captureDraftFromDom();
  if (!data) {
    root.innerHTML = '<main class="loading"><div class="spinner"></div><p>Independent EMR Core를 불러오는 중입니다.</p></main>';
    return;
  }

  const patient = currentPatient();
  root.innerHTML = `
    <header class="topbar">
      <div class="brand"><span class="mark">LYS</span><div><h1>OrthoOS <b>V0.5C</b></h1><p>Independent Orthopedic EMR · Clinical Cockpit</p></div></div>
      <div class="identity">
        <span class="demo-badge">${apiConnected ? "가상환자 DEMO" : "읽기 전용 PREVIEW"}</span>
        <div><strong>${escapeHtml(data.context.hospital.name)}</strong><span>${escapeHtml(data.context.user.displayName)} · ${escapeHtml(roleLabel[data.context.user.role] ?? data.context.user.role)}</span></div>
      </div>
    </header>
    ${notice ? `<div class="notice ${notice.kind}" role="status">${escapeHtml(notice.text)}</div>` : ""}
    ${renderFlowStrip()}
    <div class="app-shell ${workspacePreferences.showDesktopQueue ? "with-queue" : "without-queue"}">
      ${
        workspacePreferences.showDesktopQueue
          ? `<aside class="queue-panel panel">
        <div class="panel-title"><div><span class="eyebrow">WAITING</span><h2>대기 환자</h2></div><b>${data.waiting.length}</b></div>
        <div class="queue-list">${data.waiting.slice(0, 8).map(patientCard).join("") || '<p class="muted">대기 환자 없음</p>'}</div>
        <button type="button" class="open-queue-full" data-action="open-queue" data-flow-stage="all">단계별 명단 전체보기</button>
      </aside>`
          : ""
      }
      <main class="workspace panel">${renderPatientWorkspace(patient)}</main>
      <details class="audit-drawer panel">
        <summary><span>${patient ? `${escapeHtml(patient.patientName)} 환자 임상 감사 로그` : "환자 임상 감사 로그"}</span><b>${patientAudit?.events.length ?? 0}</b></summary>
        ${
          patientAuditLoading
            ? '<div class="mini-loading"><div class="spinner"></div><span>환자 임상 감사 로그를 불러오는 중</span></div>'
            : patientAuditError
              ? `<p class="inline-error">${escapeHtml(patientAuditError)}</p>`
              : `<ol class="audit-list">${patientAudit?.events.map(auditRow).join("") || '<li class="muted">이 환자의 임상 감사 기록 없음</li>'}</ol>`
        }
      </details>
    </div>
    ${renderTimelineDialog(patient)}
    ${renderRecordDialog()}
    ${renderQueueDialog()}
    ${renderLayoutDialog()}
    ${renderReminderAlertDialog(patient)}
    ${renderMobileNav()}
    <footer>LYS OrthoOS V0.5C · 가상환자 개발 Preview · 실제 진료 사용 금지</footer>`;
  queueMicrotask(syncDialogs);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${error.message} · 요청번호 ${error.requestId}`;
  if (error instanceof Error) return error.message;
  return "알 수 없는 오류가 발생했습니다.";
}

async function refreshPatientAudit(patientId: string): Promise<void> {
  const requestSequence = ++patientAuditRequestSequence;
  patientAuditLoading = true;
  patientAuditError = null;
  try {
    const result = await loadPatientAudit(patientId);
    if (requestSequence !== patientAuditRequestSequence) return;
    if (currentPatient()?.patientId !== patientId) return;
    patientAudit = result;
  } catch (error) {
    if (requestSequence !== patientAuditRequestSequence) return;
    if (currentPatient()?.patientId !== patientId) return;
    patientAudit = null;
    patientAuditError = errorMessage(error);
  } finally {
    if (requestSequence === patientAuditRequestSequence && currentPatient()?.patientId === patientId) {
      patientAuditLoading = false;
    }
  }
}

function applyLoadedRecord(patient: WaitingPatient, record: EncounterRecordResponse): void {
  currentEncounterRecord = record;
  recordCache.set(record.encounter.id, record);
  const document = record.documents.find((candidate) => candidate.type === "progress_note");
  if (draftEncounterId !== patient.encounterId || !draftDirty) {
    draftEncounterId = patient.encounterId;
    draftTitle = document?.title ?? "";
    draftContent = document?.content ?? createDefaultDraft(patient);
    draftVersion = document?.version ?? 0;
    draftSavedAt = document?.updatedAt ?? null;
    draftDirty = false;
  }
}

async function loadClinicalWorkspace(
  patient: WaitingPatient,
  notifyAboutReminders = false,
): Promise<void> {
  const requestSequence = ++clinicalRequestSequence;
  const switchingEncounter = draftEncounterId !== patient.encounterId;
  if (switchingEncounter) {
    draftEncounterId = patient.encounterId;
    draftTitle = "";
    draftContent = null;
    draftVersion = 0;
    draftSavedAt = null;
    draftDirty = false;
    timelineOverview = null;
    timelineResults = null;
    encounterOrders = null;
    ordersError = null;
    orderSearchText = "";
    orderModality = "xray";
    orderBodyRegion = "all";
    orderLaterality = patient.reason.includes("좌측")
      ? "left"
      : patient.reason.includes("양측")
        ? "bilateral"
        : "right";
    voiceResults = [];
    voiceError = null;
    contextTab = "clinical";
    mobileTab = "chart";
    voiceDraftOrderId = null;
    assistantConversation = [];
    assistantError = null;
    reminders = [];
    remindersError = null;
    reminderAlertOpen = false;
    patientAudit = null;
    patientAuditError = null;
    patientAuditRequestSequence += 1;
  }
  clinicalLoading = true;
  ordersLoading = true;
  remindersBusy = true;
  patientAuditLoading = true;
  clinicalError = null;
  timelineError = null;
  patientAuditError = null;
  render();

  const [timelineResult, recordResult, ordersResult, reminderResult, patientAuditResult] =
    await Promise.allSettled([
    loadPatientTimeline(patient.patientId),
    loadEncounterRecord(patient.encounterId),
    loadEncounterOrders(patient.encounterId),
    loadPatientReminders(patient.patientId),
    loadPatientAudit(patient.patientId),
  ]);
  if (requestSequence !== clinicalRequestSequence) return;

  if (timelineResult.status === "fulfilled") {
    timelineOverview = timelineResult.value;
    timelineResults = timelineResult.value;
  } else {
    timelineError = errorMessage(timelineResult.reason);
  }
  if (recordResult.status === "fulfilled") {
    applyLoadedRecord(patient, recordResult.value);
  } else {
    clinicalError = errorMessage(recordResult.reason);
    if (!draftContent) draftContent = createDefaultDraft(patient);
  }
  if (ordersResult.status === "fulfilled") {
    encounterOrders = ordersResult.value;
    ordersError = null;
  } else {
    encounterOrders = null;
    ordersError = errorMessage(ordersResult.reason);
  }
  if (reminderResult.status === "fulfilled") {
    reminders = reminderResult.value.reminders;
    remindersError = null;
    if (
      notifyAboutReminders &&
      reminders.some((reminder) => reminder.status === "active")
    ) {
      reminderAlertOpen = true;
      void playReminderTone();
    }
  } else {
    reminders = [];
    remindersError = errorMessage(reminderResult.reason);
  }
  if (patientAuditResult.status === "fulfilled") {
    patientAudit = patientAuditResult.value;
    patientAuditError = null;
  } else {
    patientAudit = null;
    patientAuditError = errorMessage(patientAuditResult.reason);
  }
  patientAuditLoading = false;
  clinicalLoading = false;
  ordersLoading = false;
  remindersBusy = false;
  render();
}

async function refresh(preferredEncounterId?: string): Promise<void> {
  try {
    data = await loadBootstrap();
    apiConnected = true;
  } catch (error) {
    apiConnected = false;
    data = previewFixture;
    notice = {
      kind: "error",
      text: `Preview D1 연결 전입니다. 가상 화면만 표시합니다. ${errorMessage(error)}`,
    };
  }
  if (preferredEncounterId && data.waiting.some((item) => item.encounterId === preferredEncounterId)) {
    selectedEncounterId = preferredEncounterId;
  } else if (!data.waiting.some((item) => item.encounterId === selectedEncounterId)) {
    selectedEncounterId = data.waiting[0]?.encounterId ?? null;
  }
  render();
  const patient = currentPatient();
  if (patient) void loadClinicalWorkspace(patient);
  const hashRecord = recordIdFromHash();
  if (hashRecord) void openRecord(hashRecord, false, false);
}

function updateEditorStatus(): void {
  const saveButton = document.querySelector<HTMLButtonElement>('[data-action="save-note"]');
  if (saveButton) saveButton.disabled = !draftDirty || noteSaving;
  const state = document.querySelector<HTMLElement>("#note-save-state");
  if (state) {
    state.textContent = noteSaving ? "저장 중…" : `미저장 변경 · 현재 v${draftVersion}`;
    state.classList.toggle("dirty", draftDirty);
  }
  const hint = document.querySelector<HTMLElement>("#editor-hint");
  if (hint) hint.textContent = editorHint;
}

function markDraftDirty(message = "변경사항을 저장하십시오."): void {
  draftDirty = true;
  editorHint = message;
  updateEditorStatus();
}

function appendVoiceFactsToDraft(
  patient: WaitingPatient,
  facts: readonly ClinicalFactSuggestion[],
): void {
  const base = draftContent ?? createDefaultDraft(patient);
  draftContent = appendClinicalFactsToStructuredDraft(base, facts);
  draftDirty = true;
  editorHint = `음성 구조화 항목 ${facts.length}개를 해당 차트 구획에 바로 누적했습니다. 저장 전 확인하십시오.`;
}

async function runVoicePreview(patient: WaitingPatient): Promise<void> {
  if (voiceBusy || patient.encounterStatus !== "in_progress") return;
  captureDraftFromDom();
  voiceBusy = true;
  voiceError = null;
  notice = { kind: "info", text: "가상 문답 원문을 구조화하고 출처를 기록하고 있습니다." };
  render();
  try {
    const voiceResult = await previewVoiceCharting(
      patient.encounterId,
      syntheticShoulderDialogue(),
    );
    voiceResults = [...voiceResults, voiceResult];
    appendVoiceFactsToDraft(patient, voiceResult.facts);
    await refreshPatientAudit(patient.patientId);
    notice = {
      kind: "ok",
      text: `이번 음성 구조화 항목 ${voiceResult.facts.length}개를 차트 구획에 반영했습니다. 현재 누적 ${voiceResults.flatMap((result) => result.facts).length}개입니다.`,
    };
  } catch (error) {
    voiceError = errorMessage(error);
    notice = { kind: "error", text: voiceError };
  } finally {
    voiceBusy = false;
    render();
  }
}

async function askJohn(
  patient: WaitingPatient,
  inputMode: "text" | "voice_preview" = "text",
): Promise<void> {
  const query = assistantQueryText.trim();
  if (!query || assistantBusy) return;
  assistantBusy = true;
  assistantError = null;
  contextTab = "assistant";
  mobileTab = "assistant";
  render();
  try {
    const response = await queryPatientAssistant(patient.patientId, {
      encounterId: patient.encounterId,
      query,
      inputMode,
    });
    assistantConversation = [...assistantConversation, response];
    await refreshPatientAudit(patient.patientId);
    notice = {
      kind: "ok",
      text: `존이 ${response.sources.length}개 근거를 구분해 답했습니다. 의사 검토가 필요합니다.`,
    };
  } catch (error) {
    assistantError = errorMessage(error);
    notice = { kind: "error", text: assistantError };
  } finally {
    assistantBusy = false;
    render();
  }
}

async function runCurrentAiCheck(patient: WaitingPatient): Promise<void> {
  if (remindersBusy || patient.encounterStatus !== "in_progress") return;
  remindersBusy = true;
  remindersError = null;
  contextTab = "reminders";
  mobileTab = "reminders";
  render();
  try {
    const response = await runAiCheck(patient.encounterId);
    reminders = response.reminders;
    reminderAlertOpen = reminders.some((reminder) => reminder.status === "active");
    await refreshPatientAudit(patient.patientId);
    notice = {
      kind: "ok",
      text: `AI 체크를 마쳤습니다. 새 알림 ${response.generated}개를 환자 차트에 저장했습니다.`,
    };
    if (reminderAlertOpen) void playReminderTone();
  } catch (error) {
    remindersError = errorMessage(error);
    notice = { kind: "error", text: remindersError };
  } finally {
    remindersBusy = false;
    render();
  }
}

async function acknowledgeCurrentReminder(reminderId: string): Promise<void> {
  if (remindersBusy) return;
  remindersBusy = true;
  remindersError = null;
  render();
  try {
    const response = await acknowledgeReminder(reminderId);
    reminders = reminders.map((reminder) =>
      reminder.id === response.reminder.id ? response.reminder : reminder,
    );
    if (!reminders.some((reminder) => reminder.status === "active")) {
      reminderAlertOpen = false;
    }
    await refreshPatientAudit(response.reminder.patientId);
    notice = { kind: "ok", text: "AI 체크 알림을 확인 처리했습니다." };
  } catch (error) {
    remindersError = errorMessage(error);
    notice = { kind: "error", text: remindersError };
  } finally {
    remindersBusy = false;
    render();
  }
}

async function changePatientFlowStage(
  receptionId: string,
  nextStage: PatientFlowStage,
): Promise<void> {
  const patient = data?.waiting.find((item) => item.receptionId === receptionId);
  if (!patient || patient.flowStage === nextStage || busy) return;
  busy = true;
  notice = { kind: "info", text: "환자 흐름 단계를 변경하고 있습니다." };
  render();
  try {
    const response = await updatePatientFlowStage(receptionId, nextStage);
    patient.flowStage = response.flowStage;
    await refreshPatientAudit(patient.patientId);
    notice = {
      kind: "ok",
      text: `${patient.patientName} 환자를 ${patientFlowStageLabels[nextStage]} 단계로 옮겼습니다.`,
    };
  } catch (error) {
    notice = { kind: "error", text: errorMessage(error) };
  } finally {
    busy = false;
    render();
  }
}

async function reloadOrders(encounterId: string): Promise<void> {
  encounterOrders = await loadEncounterOrders(encounterId);
  ordersError = null;
}

async function addOrderFromCatalog(
  patient: WaitingPatient,
  catalogItemId: string,
  source: "click" | "voice_preview" = "click",
): Promise<void> {
  if (orderBusyId || patient.encounterStatus !== "in_progress") return;
  orderBusyId = catalogItemId;
  ordersError = null;
  notice = {
    kind: "info",
    text:
      source === "voice_preview"
        ? "가상 음성에서 검사명·부위·좌우를 추출해 오더 초안만 만들고 있습니다."
        : "오더 초안을 만들고 있습니다.",
  };
  render();
  try {
    const catalogItem = encounterOrders?.catalog.find(
      (item) => item.id === catalogItemId,
    );
    if (!catalogItem) throw new Error("오더 항목을 다시 불러오십시오.");
    const created = await createOrderDraft(patient.encounterId, {
      catalogItemId,
      laterality: catalogItem.requiresLaterality ? orderLaterality : "not_applicable",
    });
    if (source === "voice_preview") voiceDraftOrderId = created.order.id;
    notice = {
      kind: "ok",
      text: `${created.order.displayName} 오더 초안을 만들었습니다. 의사가 “오더 완료”라고 확인하기 전에는 전송되지 않습니다.`,
    };
    await reloadOrders(patient.encounterId);
    await refreshPatientAudit(patient.patientId);
  } catch (error) {
    ordersError = errorMessage(error);
    notice = { kind: "error", text: ordersError };
  } finally {
    orderBusyId = null;
    render();
  }
}

async function confirmCurrentOrder(
  patient: WaitingPatient,
  orderId: string,
): Promise<void> {
  if (orderBusyId || patient.encounterStatus !== "in_progress") return;
  const order = encounterOrders?.orders.find((candidate) => candidate.id === orderId);
  if (!order) return;
  orderBusyId = orderId;
  notice = { kind: "info", text: "환자·검사·부위·좌우를 확인하고 작업목록에 접수하고 있습니다." };
  render();
  try {
    const result = await confirmOrder(order.id, order.version);
    await reloadOrders(patient.encounterId);
    if (voiceDraftOrderId === order.id) voiceDraftOrderId = null;
    let flowNotice = "";
    if (patient.flowStage === "waiting") {
      try {
        const flow = await updatePatientFlowStage(patient.receptionId, "pre_test");
        patient.flowStage = flow.flowStage;
        flowNotice = " 환자 흐름은 검사 전으로 이동했습니다.";
      } catch {
        flowNotice = " 환자 흐름 단계는 명단에서 별도로 확인하십시오.";
      }
    }
    await refreshPatientAudit(patient.patientId);
    notice = {
      kind: "ok",
      text: `${result.order.displayName} 오더가 확정되어 ${result.order.destination === "radiology" ? "방사선실" : "검사실"}에 접수됐습니다.${flowNotice} 진료 종료 전 AI 체크를 권장합니다.`,
    };
  } catch (error) {
    ordersError = errorMessage(error);
    notice = { kind: "error", text: ordersError };
  } finally {
    orderBusyId = null;
    render();
  }
}

function runSyntheticVoiceOrderDraft(patient: WaitingPatient): void {
  const item = encounterOrders?.catalog.find(
    (candidate) => candidate.code === "XR-SHOULDER-AP-LAT",
  );
  if (!item) {
    ordersError = "Shoulder AP + Lateral 오더 항목을 찾을 수 없습니다.";
    render();
    return;
  }
  void addOrderFromCatalog(patient, item.id, "voice_preview");
}

function confirmSyntheticVoiceOrder(patient: WaitingPatient): void {
  if (!voiceDraftOrderId) return;
  void confirmCurrentOrder(patient, voiceDraftOrderId);
}

function runFormat(format: ClinicalInlineFormat, label: string): void {
  const editor = document.querySelector<HTMLElement>("#clinical-editor");
  if (!editor || !applyInlineFormat(editor, format)) {
    editorHint = "먼저 서식을 적용할 문장을 선택하십시오.";
    updateEditorStatus();
    return;
  }
  draftContent = serializeClinicalEditor(editor);
  markDraftDirty(`${label} 서식을 적용했습니다.`);
}

async function saveCurrentNote(): Promise<void> {
  const patient = currentPatient();
  const editor = document.querySelector<HTMLElement>("#clinical-editor");
  if (!patient || !editor || patient.encounterStatus !== "in_progress" || noteSaving) return;
  try {
    draftContent = serializeClinicalEditor(editor);
  } catch (error) {
    notice = { kind: "error", text: errorMessage(error) };
    render();
    return;
  }
  if (!draftContent) return;
  noteSaving = true;
  notice = { kind: "info", text: "구조화 임상 기록의 새 버전을 저장하고 있습니다." };
  render();
  try {
    const result = await saveClinicalNote(patient.encounterId, {
      expectedVersion: draftVersion,
      title: draftTitle,
      content: draftContent,
    });
    draftVersion = result.version;
    draftSavedAt = result.savedAt;
    draftDirty = false;
    recordCache.delete(patient.encounterId);
    currentEncounterRecord = await loadEncounterRecord(patient.encounterId);
    recordCache.set(patient.encounterId, currentEncounterRecord);
    await refreshPatientAudit(patient.patientId);
    notice = { kind: "ok", text: `임상 기록 버전 ${result.version}을 감사로그와 함께 저장했습니다.` };
  } catch (error) {
    notice = { kind: "error", text: errorMessage(error) };
  } finally {
    noteSaving = false;
    render();
  }
}

async function searchTimeline(): Promise<void> {
  const patient = currentPatient();
  if (!patient) return;
  const requestSequence = ++timelineRequestSequence;
  timelineLoading = true;
  timelineError = null;
  render();
  try {
    const result = await loadPatientTimeline(patient.patientId, {
      text: timelineSearchText,
      type: timelineType,
    });
    if (requestSequence !== timelineRequestSequence) return;
    timelineResults = result;
  } catch (error) {
    if (requestSequence !== timelineRequestSequence) return;
    timelineError = errorMessage(error);
  } finally {
    if (requestSequence === timelineRequestSequence) {
      timelineLoading = false;
      render();
    }
  }
}

function recordIdFromHash(): string | null {
  const match = /^#record=([^&]+)$/.exec(window.location.hash);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

async function openRecord(
  encounterId: string,
  shouldReturnToTimeline: boolean,
  updateHash = true,
): Promise<void> {
  if (!encounterId) return;
  captureDraftFromDom();
  returnToTimeline = shouldReturnToTimeline;
  timelineOpen = false;
  recordOpen = true;
  recordEncounterId = encounterId;
  recordError = null;
  const cached = recordCache.get(encounterId);
  recordDetail = cached ?? null;
  recordLoading = !cached;
  if (updateHash) window.history.pushState(null, "", `#record=${encodeURIComponent(encounterId)}`);
  render();
  if (cached) return;
  try {
    const detail = await loadEncounterRecord(encounterId);
    if (recordEncounterId !== encounterId) return;
    recordCache.set(encounterId, detail);
    recordDetail = detail;
  } catch (error) {
    if (recordEncounterId !== encounterId) return;
    recordError = errorMessage(error);
  } finally {
    if (recordEncounterId === encounterId) {
      recordLoading = false;
      render();
    }
  }
}

function closeRecord(updateHash = true): void {
  recordOpen = false;
  recordLoading = false;
  recordError = null;
  recordDetail = null;
  recordEncounterId = null;
  if (updateHash && recordIdFromHash()) {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }
  if (returnToTimeline) timelineOpen = true;
  returnToTimeline = false;
  render();
}

root.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!target) return;
  const action = target.dataset.action;

  if (action === "select") {
    if (busy) return;
    captureDraftFromDom();
    if (draftDirty && target.dataset.encounterId !== selectedEncounterId) {
      notice = { kind: "error", text: "현재 환자의 임상 기록을 저장한 뒤 다른 환자를 선택하십시오." };
      render();
      return;
    }
    selectedEncounterId = target.dataset.encounterId ?? null;
    queueOpen = false;
    notice = null;
    render();
    const patient = currentPatient();
    if (patient) void loadClinicalWorkspace(patient, true);
    return;
  }

  if (action === "open-queue") {
    const stage = target.dataset.flowStage;
    queueFilter = patientFlowStages.includes(stage as PatientFlowStage)
      ? (stage as PatientFlowStage)
      : null;
    queueOpen = true;
    render();
    return;
  }
  if (action === "close-queue") {
    queueOpen = false;
    render();
    return;
  }
  if (action === "queue-filter") {
    const stage = target.dataset.flowStage;
    queueFilter = patientFlowStages.includes(stage as PatientFlowStage)
      ? (stage as PatientFlowStage)
      : null;
    render();
    return;
  }
  if (action === "open-layout") {
    layoutOpen = true;
    render();
    return;
  }
  if (action === "close-layout") {
    layoutOpen = false;
    render();
    return;
  }
  if (action === "layout-preset") {
    const preset = target.dataset.preset as WorkspacePreset;
    if (preset === "chart_focus" || preset === "balanced" || preset === "order_focus") {
      workspacePreferences = preferencesForPreset(preset, workspacePreferences);
      saveWorkspacePreferences(workspacePreferences);
      render();
    }
    return;
  }
  if (action === "mobile-tab") {
    const tab = target.dataset.tab;
    if (
      tab === "chart" ||
      tab === "orders" ||
      tab === "history" ||
      tab === "assistant" ||
      tab === "reminders"
    ) {
      mobileTab = tab;
      if (tab === "history") contextTab = "clinical";
      if (tab === "assistant") contextTab = "assistant";
      if (tab === "reminders") contextTab = "reminders";
      render();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    return;
  }
  if (action === "close-reminder-alert") {
    reminderAlertOpen = false;
    render();
    return;
  }
  if (action === "open-reminders-tab") {
    reminderAlertOpen = false;
    contextTab = "reminders";
    mobileTab = "reminders";
    render();
    return;
  }

  if (action === "open-timeline") {
    timelineSearchText = "";
    timelineType = null;
    timelineResults = timelineOverview;
    timelineError = null;
    timelineOpen = true;
    render();
    return;
  }
  if (action === "open-timeline-filter") {
    const rawType = target.dataset.eventType;
    timelineSearchText = "";
    timelineType = rawType as ClinicalEventType;
    timelineResults = timelineOverview;
    timelineError = null;
    timelineOpen = true;
    render();
    void searchTimeline();
    return;
  }
  if (action === "close-timeline") {
    timelineOpen = false;
    render();
    return;
  }
  if (action === "timeline-filter") {
    const rawType = target.dataset.eventType;
    timelineType = rawType === "all" ? null : (rawType as ClinicalEventType);
    void searchTimeline();
    return;
  }
  if (action === "open-record") {
    event.preventDefault();
    const encounterId = target.dataset.encounterId;
    if (encounterId) void openRecord(encounterId, timelineOpen);
    return;
  }
  if (action === "close-record") {
    closeRecord();
    return;
  }
  if (action === "context-tab") {
    contextTab =
      target.dataset.tab === "assistant"
        ? "assistant"
        : target.dataset.tab === "reminders"
          ? "reminders"
          : "clinical";
    mobileTab =
      contextTab === "clinical" ? "history" : contextTab;
    render();
    return;
  }

  if (action === "format-bold") {
    runFormat({ type: "bold" }, "굵게");
    return;
  }
  if (action === "format-italic") {
    runFormat({ type: "italic" }, "기울임");
    return;
  }
  if (action === "format-keyword") {
    runFormat({ type: "keyword" }, "핵심어");
    return;
  }
  if (action === "auto-keywords") {
    const editor = document.querySelector<HTMLElement>("#clinical-editor");
    if (!editor) return;
    draftContent = applyAutomaticKeywordEmphasis(editor);
    markDraftDirty("정형외과 핵심단어를 자동으로 굵게 표시했습니다.");
    return;
  }
  if (action === "save-note") {
    void saveCurrentNote();
    return;
  }

  const patient = currentPatient();
  if (!patient) return;
  if (action === "assistant-quick") {
    assistantQueryText = target.dataset.query ?? "";
    void askJohn(patient);
    return;
  }
  if (action === "assistant-voice-preview") {
    void askJohn(patient, "voice_preview");
    return;
  }
  if (action === "speak-assistant") {
    const latest = assistantConversation.at(-1);
    if (!latest) return;
    try {
      speakKorean(latest.speakableText, privateAudioConfirmed);
      notice = {
        kind: "info",
        text: "존의 답변을 OS 기본 오디오 출력으로 재생합니다.",
      };
    } catch (error) {
      notice = { kind: "error", text: errorMessage(error) };
    }
    render();
    return;
  }
  if (action === "order-modality") {
    const modality = target.dataset.modality as OrderModality;
    if (modality in orderModalityLabel) {
      orderModality = modality;
      orderBodyRegion = "all";
      orderSearchText = "";
      render();
    }
    return;
  }
  if (action === "order-body-region") {
    orderBodyRegion = target.dataset.bodyRegion ?? "all";
    render();
    return;
  }
  if (action === "open-clinical-microphone") {
    window.dispatchEvent(new CustomEvent("lys:microphone-panel-open"));
    notice = {
      kind: "info",
      text: "음성진료 마이크를 열었습니다. 녹음은 사용자가 시작 버튼을 눌러야 시작됩니다.",
    };
    render();
    return;
  }
  if (action === "run-voice-preview") {
    void runVoicePreview(patient);
    return;
  }
  if (action === "add-order") {
    const catalogItemId = target.dataset.catalogId;
    if (catalogItemId) void addOrderFromCatalog(patient, catalogItemId);
    return;
  }
  if (action === "confirm-order") {
    const orderId = target.dataset.orderId;
    if (orderId) void confirmCurrentOrder(patient, orderId);
    return;
  }
  if (action === "run-voice-order-draft") {
    runSyntheticVoiceOrderDraft(patient);
    return;
  }
  if (action === "confirm-voice-order") {
    confirmSyntheticVoiceOrder(patient);
    return;
  }
  if (action === "run-ai-check") {
    void runCurrentAiCheck(patient);
    return;
  }
  if (action === "ack-reminder") {
    const reminderId = target.dataset.reminderId;
    if (reminderId) void acknowledgeCurrentReminder(reminderId);
    return;
  }
  if (busy) return;
  if ((action === "start" || action === "complete") && apiConnected) {
    captureDraftFromDom();
    if (action === "complete" && draftDirty) {
      notice = { kind: "error", text: "미저장 임상 기록이 있습니다. 기록 저장 후 진료를 완료하십시오." };
      render();
      return;
    }
    busy = true;
    notice = { kind: "info", text: "상태 변경을 기록하고 있습니다." };
    render();
    const operation = action === "start" ? startEncounter : completeEncounter;
    void operation(patient.encounterId)
      .then(async () => {
        notice = {
          kind: "ok",
          text: action === "start" ? "진료 Session을 시작했습니다." : "진료를 완료했습니다.",
        };
        await refresh(patient.encounterId);
      })
      .catch((error: unknown) => {
        notice = { kind: "error", text: errorMessage(error) };
      })
      .finally(() => {
        busy = false;
        render();
      });
  }
});

root.addEventListener("input", (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.id === "clinical-editor") {
    draftContent = serializeClinicalEditor(target);
    markDraftDirty();
    return;
  }
  if (target instanceof HTMLInputElement && target.id === "note-title") {
    draftTitle = target.value;
    markDraftDirty("기록 제목이 변경되었습니다.");
    return;
  }
  if (target instanceof HTMLInputElement && target.id === "timeline-search") {
    timelineSearchText = target.value;
    if (timelineSearchTimer !== null) window.clearTimeout(timelineSearchTimer);
    timelineSearchTimer = window.setTimeout(() => {
      timelineSearchTimer = null;
      void searchTimeline();
    }, 300);
    return;
  }
  if (target instanceof HTMLInputElement && target.id === "order-search") {
    orderSearchText = target.value;
    const cursor = target.selectionStart ?? target.value.length;
    render();
    queueMicrotask(() => {
      const replacement = document.querySelector<HTMLInputElement>("#order-search");
      replacement?.focus();
      replacement?.setSelectionRange(cursor, cursor);
    });
    return;
  }
  if (target instanceof HTMLInputElement && target.id === "assistant-query") {
    assistantQueryText = target.value;
    return;
  }
  if (target instanceof HTMLInputElement && target.id === "chart-percent") {
    const value = Number(target.value);
    if (Number.isFinite(value)) {
      workspacePreferences = {
        ...workspacePreferences,
        chartPercent: Math.min(72, Math.max(48, Math.round(value))),
      };
      saveWorkspacePreferences(workspacePreferences);
      const label = target.closest("label")?.querySelector("b");
      if (label) label.textContent = `${workspacePreferences.chartPercent}%`;
      document
        .querySelector<HTMLElement>(".clinical-cockpit")
        ?.style.setProperty(
          "--chart-fr",
          `${(workspacePreferences.chartPercent / 44).toFixed(2)}fr`,
        );
    }
  }
});

root.addEventListener("change", (event) => {
  const target = event.target;
  if (target instanceof HTMLSelectElement && target.id === "format-color") {
    const value = target.value as ClinicalTextColor | "";
    if (value) runFormat({ type: "color", value }, "글자색");
    target.value = "";
  }
  if (target instanceof HTMLSelectElement && target.id === "format-size") {
    const value = target.value as ClinicalTextSize | "";
    if (value) runFormat({ type: "size", value }, "글자 크기");
    target.value = "";
  }
  if (target instanceof HTMLSelectElement && target.id === "order-laterality") {
    const value = target.value as OrderLaterality;
    if (value === "left" || value === "right" || value === "bilateral" || value === "not_applicable") {
      orderLaterality = value;
      render();
    }
  }
  if (target instanceof HTMLSelectElement && target.id === "order-sort") {
    const value = target.value as OrderCatalogSort;
    if (
      value === "frequency" ||
      value === "body_region" ||
      value === "alphabetical" ||
      value === "shorthand"
    ) {
      workspacePreferences = { ...workspacePreferences, orderSort: value };
      saveWorkspacePreferences(workspacePreferences);
      render();
    }
  }
  if (target instanceof HTMLSelectElement && target.classList.contains("flow-stage-select")) {
    const receptionId = target.dataset.receptionId;
    const nextStage = target.value as PatientFlowStage;
    if (receptionId && patientFlowStages.includes(nextStage)) {
      void changePatientFlowStage(receptionId, nextStage);
    }
  }
  if (target instanceof HTMLInputElement && target.id === "private-audio-confirm") {
    privateAudioConfirmed = target.checked;
    if (!privateAudioConfirmed) stopSpeaking();
    render();
  }
  if (target instanceof HTMLInputElement && target.id === "desktop-queue-toggle") {
    workspacePreferences = {
      ...workspacePreferences,
      showDesktopQueue: target.checked,
    };
    saveWorkspacePreferences(workspacePreferences);
    render();
  }
});

root.addEventListener("paste", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.id !== "clinical-editor") return;
  event.preventDefault();
  const plainText = event.clipboardData?.getData("text/plain") ?? "";
  if (insertPlainTextAtSelection(target, plainText)) {
    draftContent = serializeClinicalEditor(target);
    markDraftDirty("붙여넣은 내용에서 외부 HTML 서식을 제거했습니다.");
  }
});

root.addEventListener("keydown", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.id !== "clinical-editor") return;
  if (!(event.ctrlKey || event.metaKey)) return;
  if (event.key.toLocaleLowerCase() === "b") {
    event.preventDefault();
    runFormat({ type: "bold" }, "굵게");
  }
  if (event.key.toLocaleLowerCase() === "i") {
    event.preventDefault();
    runFormat({ type: "italic" }, "기울임");
  }
});

root.addEventListener(
  "cancel",
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDialogElement)) return;
    event.preventDefault();
    if (target.id === "timeline-dialog") {
      timelineOpen = false;
      render();
    }
    if (target.id === "record-dialog") closeRecord();
    if (target.id === "queue-dialog") {
      queueOpen = false;
      render();
    }
    if (target.id === "layout-dialog") {
      layoutOpen = false;
      render();
    }
    if (target.id === "reminder-alert-dialog") {
      reminderAlertOpen = false;
      render();
    }
  },
  true,
);

root.addEventListener("submit", (event) => {
  const form = event.target;
  if (form instanceof HTMLFormElement && form.id === "assistant-form") {
    event.preventDefault();
    const patient = currentPatient();
    if (patient) void askJohn(patient);
    return;
  }
  if (
    !(form instanceof HTMLFormElement) ||
    form.id !== "register-form" ||
    busy ||
    !apiConnected
  ) {
    return;
  }
  event.preventDefault();
  const formData = new FormData(form);
  const input: RegisterPatientRequest = {
    chartNumber: String(formData.get("chartNumber") ?? ""),
    patientName: String(formData.get("patientName") ?? ""),
    dateOfBirth: String(formData.get("dateOfBirth") ?? ""),
    sexAtBirth: String(formData.get("sexAtBirth") ?? "unknown") as RegisterPatientRequest["sexAtBirth"],
    reason: String(formData.get("reason") ?? ""),
  };

  busy = true;
  notice = { kind: "info", text: "가상환자를 접수하고 있습니다." };
  render();
  void registerPatient(input)
    .then(async (result) => {
      notice = { kind: "ok", text: "가상환자를 대기열에 등록했습니다." };
      await refresh(result.encounterId);
    })
    .catch((error: unknown) => {
      notice = { kind: "error", text: errorMessage(error) };
    })
    .finally(() => {
      busy = false;
      render();
    });
});

window.addEventListener("hashchange", () => {
  const encounterId = recordIdFromHash();
  if (encounterId) void openRecord(encounterId, false, false);
  else if (recordOpen) closeRecord(false);
});

render();
void refresh();
